import { WorkflowRole } from '@exam-management/common/types';
import { WorkflowStepCode } from '@exam-management/graphql';

export enum Ability {
  VIEW_MAIN_TASK_DETAILS = 'VIEW_MAIN_TASK_DETAILS',
  VIEW_SUBTASK_DETAILS = 'VIEW_SUBTASK_DETAILS',
  VIEW_SUBTASKS = 'VIEW_SUBTASKS',
  ADD_SUBTASK = 'ADD_SUBTASK',
  CREATE_UPDATE_REVIEW = 'CREATE_UPDATE_REVIEW',
  FINISH_TASK = 'FINISH_TASK',
}

export enum TaskType {
  TASK = 'TASK',
  SUBTASK = 'SUBTASK',
}

interface GetQuestionReviewTaskAbilityParams {
  workflowRoles: WorkflowRole[];
  status: WorkflowStepCode;
  ability: (keyof typeof Ability)[];
  taskType: (keyof typeof TaskType)[];
}

export function getQuestionReviewTaskAbility(params: GetQuestionReviewTaskAbilityParams) {
  const { workflowRoles, status, ability, taskType } = params;
  const isTaskOwner = workflowRoles.includes(WorkflowRole.TASK_OWNER);
  const isSubtaskAssignee = workflowRoles.includes(WorkflowRole.SUBTASK_ASSIGNEE);

  const isTodo = status === WorkflowStepCode.QuestionReviewTodo;
  const isInReview = status === WorkflowStepCode.QuestionReviewInReview;
  const isReviewed = status === WorkflowStepCode.QuestionReviewReviewed;
  const isDone = status === WorkflowStepCode.QuestionReviewDone;

  const abilityMap: Record<TaskType, Record<Ability, boolean>> = {
    [TaskType.TASK]: {
      [Ability.VIEW_MAIN_TASK_DETAILS]:
        isTaskOwner && (isTodo || isInReview || isReviewed || isDone),
      [Ability.VIEW_SUBTASK_DETAILS]: false,
      [Ability.VIEW_SUBTASKS]: isTaskOwner,
      [Ability.ADD_SUBTASK]: isTaskOwner && (isTodo || isInReview || isReviewed),
      [Ability.CREATE_UPDATE_REVIEW]: false,
      [Ability.FINISH_TASK]: isTaskOwner && isReviewed,
    },
    [TaskType.SUBTASK]: {
      [Ability.VIEW_MAIN_TASK_DETAILS]: false,
      [Ability.VIEW_SUBTASK_DETAILS]:
        isSubtaskAssignee && (isTodo || isInReview || isReviewed || isDone),
      [Ability.VIEW_SUBTASKS]: false,
      [Ability.ADD_SUBTASK]: false,
      [Ability.CREATE_UPDATE_REVIEW]: isSubtaskAssignee && (isTodo || isInReview),
      [Ability.FINISH_TASK]: isSubtaskAssignee && isInReview,
    },
  };

  return taskType.some((task) => ability.some((ability) => abilityMap[task][ability as Ability]));
}
